export interface ExportFileConfig {
  fileName?: string;
  headers?: string[];
  mappings?: Record<string, string | ((item: any) => string)>;
  delimiter?: string;
}

type ExtractValue = string | number | boolean | null | undefined;
type ExtractorFunction<T> = (item: T) => ExtractValue;

export class ExportFileUtils {
  private static formatValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'object') {
      if (value instanceof Date) {
        return value.toLocaleDateString();
      }
      if (Array.isArray(value)) {
        return value.join(';');
      }
      return JSON.stringify(value);
    }

    const stringValue = String(value);
    if (stringValue.includes(',') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
  }

  private static getNestedValue<T>(obj: T, path: string): ExtractValue {
    const value = path.split('.').reduce((current: any, key: string) => {
      return current && typeof current === 'object' ? current[key] : undefined;
    }, obj);

    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      value === null ||
      value === undefined
    ) {
      return value;
    }

    return String(value);
  }

  static convertToCSV<T extends Record<string, any>>(
    data: T[],
    config: ExportFileConfig = {},
  ): string {
    if (!data.length) {
      return '';
    }

    const { headers, mappings, delimiter = ',' } = config;

    let csvHeaders: string[];
    let valueExtractors: ExtractorFunction<T>[];

    if (mappings) {
      csvHeaders = Object.keys(mappings);
      valueExtractors = csvHeaders.map((header) => {
        const mapping = mappings[header];
        return (item: T): ExtractValue => {
          if (typeof mapping === 'function') {
            return mapping(item);
          }
          return this.getNestedValue(item, mapping);
        };
      });
    } else {
      const firstItem = data[0];
      csvHeaders = headers || Object.keys(firstItem);
      valueExtractors = csvHeaders.map(
        (header) =>
          (item: T): ExtractValue =>
            this.getNestedValue(item, header),
      );
    }

    const headerRow = csvHeaders.join(delimiter);
    const dataRows = data.map((item) =>
      valueExtractors
        .map((extractor) => this.formatValue(extractor(item)))
        .join(delimiter),
    );

    return [headerRow, ...dataRows].join('\n');
  }

  static downloadCSV<T extends Record<string, any>>(
    data: T[],
    config: ExportFileConfig = {},
    onError?: (error: Error) => void,
  ): void {
    try {
      const { fileName = 'export.csv' } = config;
      const csvContent = this.convertToCSV(data, config);

      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], {
        type: 'text/csv;charset=utf-8;',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error instanceof Error) {
        if (onError) {
          onError(error);
        }
        throw error;
      }
      const genericError = new Error('An unknown error occurred during export');

      if (onError) {
        onError(genericError);
      }
      throw genericError;
    }
  }
}
